<script setup lang="ts">
import Button from '~/components/design-system/button.vue';
import GenericError from '~/components/user-interface/generic-error.vue';
import { useSiteStore } from '~/stores/site.store';
import { useServices } from '~/composables/useServices';
import { globalKeys } from 'assets/data/global-keys';
import type { IInfoResponse } from '~/interfaces/dto/marketing/response';
import type { IFormBuilderReference } from '~/interfaces/form/formBuilder';
import { loginRoutes } from 'assets/data/config';

const { $t, $trackEvent, $enabled } = useNuxtApp();
const site = useSiteStore();
const route = useRoute();
const router = useRouter();
const { $authService, $walletService, $storageService } = useServices();
const prepareMobileNumber = usePrepareMobileNumber;

const form = ref<IFormBuilderReference>();

const activeLoader = ref<boolean>(true);
const errorCode = ref<string>('');
const errorRef = ref<string>('');

const sumSubEnabled = computed(() =>
  $enabled('security.highriskeventschangelogin'),
);

const deviceInfo = $storageService.getUniversalStorage<IInfoResponse>({
  key: globalKeys.siteConfigKeys.deviceInfo,
});

const model = ref({
  username: '',
  password: '',
});

const login = async () => {
  if (form.value?.invalid) return;
  activeLoader.value = true;
  $trackEvent('LoginEvent', 'LOGIN_INIT', {
    msisdn: model?.value?.username,
  });
  const { executeRecaptcha } = await useRecaptcha();

  const request = {
    username: prepareMobileNumber(model.value.username),
    password: model.value.password,
    sessionMetadata: {
      sessionTrackingToken: deviceInfo?.hash,
      uip: deviceInfo?.IpAddress,
    },
    ...(form.value?.hasRecaptcha && {
      recaptcha: await executeRecaptcha('login'),
    }),
  };
  try {
    const data = await $authService.login(request);
    if (data?.isSuccessful) {
      await $walletService.fetchBalances();
      routeToCurrentGame();
      $trackEvent('LoginEvent', 'LOGIN_SUCCESS', {
        msisdn: request?.username,
      });
      return data;
    } else {
      checkErrors(data?.error || {});
      $trackEvent('LoginEvent', 'LOGIN_FAILURE', {
        msisdn: request?.username,
        reason: data?.error?.message || data?.cause?.message,
      });
    }
  } catch (error) {
    errorCode.value = 'something-went-wrong';
    console.error({ error });
  } finally {
    activeLoader.value = false;
  }
};

const handleError = (event: string) => (errorCode.value = event);

const routeToCurrentGame = () => {
  if (route.query.redirect) {
    const splitUrl = (route.query.redirect as string).split('?');
    const queryparams = new URLSearchParams(splitUrl[1]);
    router.push({ path: splitUrl[0], query: Object.fromEntries(queryparams) });
  }
  if (route.query.freePlay) {
    const clonedQuery = Object.assign({}, route.query);
    delete clonedQuery.freePlay;
    router.replace({ query: clonedQuery });
  }
};

const checkErrors = (error: { displayCode?: string; code?: string }) => {
  console.error(error);
  if (!!error) {
    errorCode.value = error?.displayCode?.toString();
    errorRef.value = error?.code?.toString();
  }
};

onBeforeMount(() => (activeLoader.value = true));

const forgotPassword = () => {
  if (sumSubEnabled.value)
    site.activateModal('sumSubPasswordResetLoggedOutLivenessCheck');
  else site.activateModal('passwordResetStepOne');
};

const handleSignUp = () => {
  if (loginRoutes.includes(route.path)) {
    navigateTo('/register');
  } else site.activateModal('register');
};
</script>
<template>
  <div
    class="login-container relative flex flex-col h-max-content bg-layer-secondary"
    :class="{ 'aspect-video': activeLoader }"
    @keyup.enter="login"
  >
    <LazyUserInterfaceGenericLoader
      v-if="activeLoader"
      :key="activeLoader.toString()"
      container
    />
    <UserInterfaceDynamicFormBuilder
      ref="form"
      v-model="model"
      template-name="login"
      :container-classes="[{ 'p-4': !form?.isEmpty }]"
      @loaded="activeLoader = false"
      @error="handleError($event)"
      @empty-schema="handleError($event)"
    >
      <template v-if="!form?.isEmpty" #form-actions="formActionProps">
        <div
          class="p-3 pt-4"
          :class="{ 'bg-base': !('no-action-bg' in $attrs) }"
        >
          <UserInterfaceModalActionBadge
            left-label="dont-have-account"
            action-text="register"
            icon-name="ChevronRightIcon"
            @action="handleSignUp"
          />
          <GenericError
            v-if="errorCode"
            class="mb-3"
            state="danger"
            :reference-code="errorRef"
          >
            <div class="flex" v-html="$t(`jpc-login-errors-${errorCode}`)" />
          </GenericError>
          <div class="min-w-full mb-2">
            <Button
              :disabled="
                !(
                  formActionProps.form?.validated &&
                  !formActionProps.form?.invalid
                )
              "
              class="text-light-50 uppercase text-base font-bold text-center justify-center items-center min-w-full px-2 py-2 mt-1 mb-1"
              group="primary"
              @click="login"
            >
              {{ $t('submit') }}
            </Button>
          </div>
          <p
            class="dark:text-primary-blue-100 text-primary-blue-300 text-xs text-center cursor-pointer my-0"
            @click="forgotPassword"
          >
            {{
              sumSubEnabled ? $t('forgot-login-details') : $t('forgot-password')
            }}
          </p>
        </div>
      </template>
    </UserInterfaceDynamicFormBuilder>
    <UserInterfaceModalActionBadge
      v-if="form?.isEmpty && !activeLoader"
      left-label="dont-have-account"
      action-text="register"
      icon-name="ChevronRightIcon"
      @action="handleSignUp"
    />
    <GenericError
      v-if="errorCode && form?.isEmpty"
      class="mb-3"
      state="danger"
      :reference-code="errorRef"
    >
      <div class="flex" v-html="$t(`jpc-login-errors-${errorCode}`)" />
    </GenericError>
  </div>
</template>
